import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiPlay, FiShare2, FiFileText, FiArrowRight, FiCheck, FiClock } from 'react-icons/fi';
import { toast } from '../Toast';
import { SUPPORT_EMAIL } from './helpContent';
import { findVideo, findGuide, guideHref, videoHref, topicHref, HELP_VIDEOS, VIDEOS_TOPIC } from './helpGuides';

const seconds = (time: string) => {
    const [m, s] = time.split(':').map(Number);
    return m * 60 + s;
};
const COLLAPSED_LINES = 4;

// spec 200:3491 "v3 / Help – Guide video" at /help/videos/:slug. No video files exist yet (user decision 2026-09-24:
// help content is front-end only), so the player is a poster with "Video coming soon"; chapters and the transcript are real text.
// Clicking a chapter or a transcript line moves the (still) playhead there and shows that line as the caption.
const HelpVideo: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const video = findVideo(slug);
    const [line, setLine] = useState(0);
    const [full, setFull] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLine(0);
        setFull(false);
    }, [slug]);

    if (!video) return <div className="v2-page help-video-v2">
        <div className="hv-crumbs"><Link to="/help">Help centre</Link></div>
        <div className="card hv-missing">
            <h1>We couldn’t find that video</h1>
            <p>It may have been renamed. Search the help centre, or email {SUPPORT_EMAIL}.</p>
            <Link className="link" to="/help">Back to the help centre <FiArrowRight /></Link>
        </div>
    </div>;

    const total = seconds(video.length);
    const now = video.transcript[line]?.time ?? '0:00';
    const nowSeconds = seconds(now);
    // the chapter the playhead is in
    const chapter = video.chapters.reduce((current, c, i) => seconds(c.time) <= nowSeconds ? i : current, 0);
    // collapsed: a few lines around the current one
    const start = Math.max(0, Math.min(line - 1, video.transcript.length - COLLAPSED_LINES));
    const lines = full ? video.transcript : video.transcript.slice(start, start + COLLAPSED_LINES);
    const guide = video.guide ? findGuide(video.guide) : undefined;
    const upNext = HELP_VIDEOS.filter(v => v.slug !== video.slug);

    const jumpTo = (time: string) => {
        const t = seconds(time);
        const index = video.transcript.reduce((found, l, i) => seconds(l.time) <= t ? i : found, 0);
        setLine(index);
    };

    const share = () => {
        const url = window.location.href;
        if (!navigator.clipboard) { toast.info(url); return; }
        navigator.clipboard.writeText(url).then(() => toast.success('Link copied'), () => toast.info(url));
    };

    const downloadTranscript = () => {
        const text = [video.heading, '', ...video.transcript.map(l => `${l.time}  ${l.text}`)].join('\r\n');
        const href = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = href;
        a.download = `${video.slug}-transcript.txt`;
        a.click();
        URL.revokeObjectURL(href);
    };

    return <div className="v2-page help-video-v2">
        <nav className="hv-crumbs" aria-label="Breadcrumb">
            <Link to="/help">Help centre</Link><span>/</span>
            <Link to={`/help?topic=${VIDEOS_TOPIC}`}>Videos</Link><span>/</span>
            <strong>{video.title}</strong>
        </nav>

        <div className="hv-row">
            <div className="hv-main">
                <div className="hv-player">
                    <div className="hv-stage">
                        <span className="hv-big-play" aria-hidden="true"><FiPlay /></span>
                        <span className="hv-soon"><FiClock /> Video coming soon</span>
                        <small>Read the chapters and transcript below in the meantime</small>
                        <div className="hv-caption">“{video.transcript[line]?.text}”</div>
                    </div>
                    <div className="hv-controls">
                        <div className="hv-progress">
                            <div className="hv-fill" style={{ width: `${(nowSeconds / total) * 100}%` }} />
                            {video.chapters.slice(1).map(c => <span key={c.time} className="hv-mark" style={{ left: `${(seconds(c.time) / total) * 100}%` }} />)}
                            <span className="hv-head" style={{ left: `${(nowSeconds / total) * 100}%` }} />
                        </div>
                        <div className="hv-buttons">
                            <button type="button" className="hv-ctl" aria-label="Play" onClick={() => toast.info('Video coming soon: read the transcript below for now')}><FiPlay /></button>
                            <span className="hv-time">{now} / {video.length}</span>
                        </div>
                    </div>
                </div>

                <div className="card hv-info">
                    <div className="hv-meta">
                        <Link className="hv-badge" to={topicHref(video.topic)}>{video.topic}</Link>
                        <span>{video.length}</span><span className="dot">·</span><span>Transcript available</span><span className="dot">·</span><span>Video coming soon</span>
                    </div>
                    <div className="hv-title-row">
                        <h1>{video.heading}</h1>
                        <button type="button" className="hv-btn" onClick={share}><FiShare2 /> Share</button>
                        <button type="button" className="hv-btn" onClick={downloadTranscript}><FiFileText /> Download Transcript</button>
                    </div>
                    <p>{video.description}</p>
                </div>

                <div className="card hv-transcript">
                    <div className="hv-section-title">
                        <span className="icon-tile"><FiFileText /></span>
                        <div><h2>Transcript</h2><p>Click a line to move to that moment</p></div>
                        <button type="button" className="link" onClick={() => setFull(!full)}>{full ? 'Show Less' : 'Show Full Transcript'} <FiArrowRight /></button>
                    </div>
                    {lines.map(l => {
                        const i = video.transcript.indexOf(l);
                        return <button key={l.time} type="button" className={`hv-line${i === line ? ' active' : ''}`} onClick={() => setLine(i)}>
                            <strong>{l.time}</strong><span>{l.text}</span>
                        </button>;
                    })}
                </div>
            </div>

            <div className="hv-rail">
                <div className="card hv-chapters">
                    <div className="hv-chapters-title"><h2>Chapters</h2><p>{video.chapters.length} parts · {video.length}</p></div>
                    {video.chapters.map((c, i) => <button key={c.time} type="button" className={`hv-chapter${i === chapter ? ' active' : ''}`} onClick={() => jumpTo(c.time)}>
                        <span className={`hv-n${i < chapter ? ' done' : ''}`}>{i < chapter ? <FiCheck /> : i + 1}</span>
                        <span className="hv-chapter-title">{c.title}</span>
                        <small>{c.time}</small>
                    </button>)}
                </div>

                {upNext.length > 0 && <div className="card hv-next">
                    <div className="hv-next-title"><h2>Up Next</h2><Link className="link" to={`/help?topic=${VIDEOS_TOPIC}`}>All Videos <FiArrowRight /></Link></div>
                    {upNext.map(v => <Link key={v.slug} className="hv-next-row" to={videoHref(v.slug)}>
                        <span className="hv-thumb"><span className="hv-small-play"><FiPlay /></span></span>
                        <span className="hv-next-text"><strong>{v.title}</strong><small>{v.length} · Video</small></span>
                    </Link>)}
                </div>}

                {guide && <div className="card hv-read">
                    <span className="icon-tile"><FiFileText /></span>
                    <strong>Prefer reading?</strong>
                    <p>The same steps are written up as a guide.</p>
                    <Link className="link" to={guideHref(guide.slug)}>Open the Written Guide <FiArrowRight /></Link>
                </div>}
            </div>
        </div>
    </div>;
};

export default HelpVideo;
